/**
 * Margin Report Table Component
 *
 * @bill/ui DataGrid migration — replaces BStockReportGrid.
 * - Derived values (Marge Brute, FC Net, etc.) are pre-computed client-side
 * - Excel export uses inter-row formulas for all derived values
 * - Percentage columns use formulas: value / CA Net
 * - Tooltips explain each value on hover
 *
 * @see US-25d.12 - Composant MarginReportTable
 * @see sprint-datagrid-migration Phase 2.3
 */

import { useMemo, useCallback } from 'react';
import { HelpCircle } from 'lucide-react';
import {
  DataGrid,
  textColumn,
  moneyColumn,
  percentColumn,
  type DataGridColumn,
  type DataGridBand,
} from '@bill/ui';
import { useCurrency } from '@/hooks';
import { transformMarginReportToGrid } from '../utils/transform-to-grid';
import { useMarginReportTooltips } from '../hooks/useMarginReportTooltips';
import { DERIVED_ROWS, type DerivedRowConfig } from '../config/margin-report.config';
import type { MarginReportResponse, MarginReportGridRow } from '@bstock/shared';

interface MarginReportTableProps {
  report: MarginReportResponse;
  loading?: boolean;
  /** Help button click handler (opens formulas drawer) */
  onHelpClick?: () => void;
}

export function MarginReportTable({ report, loading, onHelpClick }: MarginReportTableProps) {
  const currency = useCurrency();

  // Raw rows from API (values in centimes)
  const rows = useMemo(() => transformMarginReportToGrid(report), [report]);

  // Reference value for percentage calculations (centimes)
  const ventesNettes = report.chiffreAffaires.ventesNettesCentimes;

  // Row lookup for derived calculations
  const rowLookup = useMemo(() => {
    const map = new Map<string, MarginReportGridRow>();
    rows.forEach((r) => map.set(r.id, r));
    return map;
  }, [rows]);

  // Calculate derived value from source rows (returns centimes)
  const calculateDerivedValue = useCallback(
    (config: DerivedRowConfig, field: 'theoValue' | 'reelValue'): number | null => {
      const values = config.sources.map((id) => {
        const row = rowLookup.get(id);
        return row ? row[field] : null;
      });
      if (values.some((v) => v === null)) return null;
      const nums = values as number[];
      switch (config.type) {
        case 'subtract':
          return nums[0] - nums[1];
        case 'add':
          return nums.reduce((a, b) => a + b, 0);
        case 'subtract_multiple':
          return nums[0] - nums.slice(1).reduce((a, b) => a + b, 0);
        case 'signed':
          return nums.reduce((acc, v, i) => acc + v * (config.signs?.[i] ?? 1), 0);
        default:
          return null;
      }
    },
    [rowLookup],
  );

  // Pre-compute derived values into enriched rows
  const enrichedRows = useMemo(() => {
    return rows.map((row) => {
      const config = DERIVED_ROWS[row.id];
      if (!config) return row;
      const enriched = { ...row };
      if (config.fields.includes('theo')) {
        enriched.theoValue = calculateDerivedValue(config, 'theoValue');
      }
      if (config.fields.includes('reel')) {
        enriched.reelValue = calculateDerivedValue(config, 'reelValue');
      }
      return enriched;
    });
  }, [rows, calculateDerivedValue]);

  // Row ID → data index (for Excel inter-row formula row references)
  const rowIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    enrichedRows.forEach((r, i) => map.set(r.id, i));
    return map;
  }, [enrichedRows]);

  // Tooltip cell renderers
  const { theoValueCell, reelValueCell, theoPctCell, reelPctCell } =
    useMarginReportTooltips({ rowLookup, ventesNettes, currency, calculateDerivedValue });

  // ---- Excel formula generators -------------------------------------------
  //
  // Derived value columns: inter-row formulas (e.g. =B3-B5 for marge-brute).
  // Percentage columns: =valueCol{row}/valueCol${caNetRow} for ALL rows.
  // Non-derived value rows fall back to the accessor (plain centimes→EUR).
  //

  const makeDerivedFormula = useCallback(
    (field: 'theo' | 'reel', valueColId: string) => {
      return ({ row, rowRef, col }: { row: MarginReportGridRow; rowRef: number; col: (id: string) => string }) => {
        const config = DERIVED_ROWS[row.id];
        if (!config || !config.fields.includes(field)) return null;

        const currentIdx = rowIndexMap.get(row.id)!;
        const offset = rowRef - currentIdx;
        const letter = col(valueColId);
        const refs = config.sources.map((id) => offset + rowIndexMap.get(id)!);

        switch (config.type) {
          case 'subtract':
            return `${letter}${refs[0]}-${letter}${refs[1]}`;
          case 'add':
            return refs.map((r) => `${letter}${r}`).join('+');
          case 'subtract_multiple':
            return `${letter}${refs[0]}-${refs.slice(1).map((r) => `${letter}${r}`).join('-')}`;
          case 'signed': {
            const signs = config.signs ?? refs.map(() => 1);
            return signs
              .map((s, i) => {
                const ref = `${letter}${refs[i]}`;
                if (i === 0) return s === -1 ? `-${ref}` : ref;
                return s === -1 ? `-${ref}` : `+${ref}`;
              })
              .join('');
          }
          default:
            return null;
        }
      };
    },
    [rowIndexMap],
  );

  const makePctFormula = useCallback(
    (valueColId: string) => {
      return ({ row, rowRef, col }: { row: MarginReportGridRow; rowRef: number; col: (id: string) => string }) => {
        const val = valueColId === 'theoValue' ? row.theoValue : row.reelValue;
        if (val === null) return null;

        const currentIdx = rowIndexMap.get(row.id)!;
        const offset = rowRef - currentIdx;
        const caNetRow = offset + rowIndexMap.get('ca-net')!;
        const letter = col(valueColId);
        return `${letter}${rowRef}/${letter}$${caNetRow}`;
      };
    },
    [rowIndexMap],
  );

  // ---- Columns + bands ----------------------------------------------------

  const bands = useMemo<DataGridBand[]>(
    () => [
      { id: 'theo', label: 'THÉORIQUE' },
      { id: 'reel', label: 'RÉEL' },
    ],
    [],
  );

  const columns = useMemo<DataGridColumn<MarginReportGridRow>[]>(
    () => [
      textColumn<MarginReportGridRow>({
        id: 'label',
        header: 'Ligne',
        accessor: 'label',
        sortable: false,
        cell: (r) => {
          const cls = [
            'whitespace-pre',
            r.rowType === 'total' ? 'font-semibold' : '',
            r.rowType === 'subitem' ? 'text-neutral-500 text-xs' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return <span className={cls}>{r.label}</span>;
        },
      }),
      moneyColumn<MarginReportGridRow>({
        id: 'theoValue',
        header: 'Valeur',
        band: 'theo',
        sortable: false,
        accessor: (r) => (r.theoValue !== null ? r.theoValue / 100 : null),
        cell: theoValueCell,
        exportFormula: makeDerivedFormula('theo', 'theoValue'),
      }),
      percentColumn<MarginReportGridRow>({
        id: 'theoPct',
        header: '%',
        band: 'theo',
        sortable: false,
        accessor: (r) =>
          r.theoValue !== null && ventesNettes > 0 ? r.theoValue / ventesNettes : null,
        cell: theoPctCell,
        exportFormula: makePctFormula('theoValue'),
      }),
      moneyColumn<MarginReportGridRow>({
        id: 'reelValue',
        header: 'Valeur',
        band: 'reel',
        sortable: false,
        accessor: (r) => (r.reelValue !== null ? r.reelValue / 100 : null),
        cell: reelValueCell,
        exportFormula: makeDerivedFormula('reel', 'reelValue'),
      }),
      percentColumn<MarginReportGridRow>({
        id: 'reelPct',
        header: '%',
        band: 'reel',
        sortable: false,
        accessor: (r) =>
          r.reelValue !== null && ventesNettes > 0 ? r.reelValue / ventesNettes : null,
        cell: reelPctCell,
        exportFormula: makePctFormula('reelValue'),
      }),
    ],
    [theoValueCell, reelValueCell, theoPctCell, reelPctCell, ventesNettes, makeDerivedFormula, makePctFormula],
  );

  return (
    <DataGrid<MarginReportGridRow>
      data={enrichedRows}
      columns={columns}
      bands={bands}
      rowKey={(r) => r.id}
      isLoading={loading}
      currency={currency}
      maxHeight="75vh"
      density="compact"
      title={
        onHelpClick ? (
          <button
            type="button"
            onClick={onHelpClick}
            className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
          >
            <HelpCircle className="w-4 h-4" />
            Formules
          </button>
        ) : undefined
      }
      exportable={{
        fileName: {
          base: 'rapport_rentabilite',
          brand: report.meta.brandName,
          period: { from: report.meta.dateFrom, to: report.meta.dateTo },
        },
        sheetName: 'Rapport Marge',
        position: 'header',
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune donnée pour cette période
        </div>
      }
    />
  );
}
